import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card, Table, Button, Space, Alert, Tag, Progress, Modal, Form, Input, Switch,
  Row, Col, Statistic, Tooltip, Popconfirm, Empty,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined,
  ShopOutlined, DollarOutlined, ThunderboltOutlined, RobotOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import api from '../libs/api';

const SITE_COLORS = [
  '#54A2CC', '#05A677', '#f5b759', '#FA5252', '#845EC2',
  '#FF6F91', '#0081CF', '#008E9B', '#C34A36', '#4D8076',
];

const STATUS_META = {
  ok:              { color: 'green',  text: 'OK' },
  fresh:           { color: 'blue',   text: 'Cached' },
  no_offers:       { color: 'orange', text: 'No priced offers' },
  robots_blocked:  { color: 'default',text: 'Blocked by robots.txt' },
  fetch_failed:    { color: 'red',    text: 'Fetch failed' },
  extract_failed:  { color: 'red',    text: 'Extraction failed' },
};

const money = (v) => v == null ? '-' : `$${Number(v).toLocaleString()}`;

const BASIS_LABEL = {
  total: null, from: 'from', per_week: '/week', per_month: '/month', unknown: '?',
};

export default function RetailPriceTrendingTab() {
  const [sites, setSites] = useState([]);
  const [offers, setOffers] = useState([]);
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Scrape progress
  const [scraping, setScraping] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '', results: [] });
  const autoRan = useRef(false);

  // Site editor
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // Web-search discovery
  const [discovering, setDiscovering] = useState(false);
  const [discoverResult, setDiscoverResult] = useState(null);

  const loadSites = useCallback(async () => {
    const res = await api.get('/api/market-intel/sites');
    const list = res.data.data || [];
    setSites(list);
    return list;
  }, []);

  const loadOffers = useCallback(async () => {
    const res = await api.get('/api/market-intel/offers', { params: { days: 180 } });
    const d = res.data.data || {};
    setOffers(d.latest || []);
    setHistory(d.history || []);
    setMeta({ model: d.model, freshWindowHours: d.freshWindowHours });
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await loadSites();
      await loadOffers();
      return list;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load');
      return [];
    } finally {
      setLoading(false);
    }
  }, [loadSites, loadOffers]);

  // Scrape sequentially so the user sees which competitor is being read, and so
  // we're never hitting several competitor sites in parallel.
  const runScrape = useCallback(async (siteList, force) => {
    const enabled = siteList.filter(s => s.enabled === 'YES');
    if (enabled.length === 0) return;

    setScraping(true);
    setProgress({ done: 0, total: enabled.length, current: enabled[0].name, results: [] });
    const results = [];
    for (let i = 0; i < enabled.length; i++) {
      const site = enabled[i];
      setProgress(p => ({ ...p, current: site.name, done: i }));
      try {
        const res = await api.post(`/api/market-intel/scrape/${site.id}`, null, {
          params: force ? { force: 1 } : {},
          timeout: 240_000,
        });
        results.push(res.data.data);
      } catch (err) {
        results.push({
          siteId: site.id, name: site.name, status: 'fetch_failed',
          message: err.response?.data?.message || err.message, offerCount: 0,
        });
      }
      setProgress(p => ({ ...p, done: i + 1, results: [...results] }));
    }
    setScraping(false);
    await loadSites();
    await loadOffers();
  }, [loadSites, loadOffers]);

  // On first open: load, then refresh anything outside the freshness window.
  // loadAll/runScrape are stable (useCallback over stable deps), and the ref
  // guard keeps the scrape to one run even under StrictMode double-invoke.
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    (async () => {
      const list = await loadAll();
      if (list.length > 0) await runScrape(list, false);
    })();
  }, [loadAll, runScrape]);

  const runDiscover = async () => {
    setDiscovering(true);
    setDiscoverResult(null);
    setError(null);
    try {
      const res = await api.post('/api/market-intel/discover',
        { query: 'home solar battery package offers Australia' },
        { timeout: 420_000 });
      setDiscoverResult(res.data.data);
      await loadSites();
      await loadOffers();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Discovery failed');
    }
    setDiscovering(false);
  };

  const openEditor = (site) => {
    setEditing(site || null);
    form.setFieldsValue(site
      ? { name: site.name, url: site.url, notes: site.notes, enabled: site.enabled === 'YES' }
      : { name: '', url: '', notes: '', enabled: true });
    setModalOpen(true);
  };

  const saveSite = async () => {
    const vals = await form.validateFields();
    try {
      if (editing) await api.put(`/api/market-intel/sites/${editing.id}`, vals);
      else await api.post('/api/market-intel/sites', vals);
      setModalOpen(false);
      await loadSites();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
    }
  };

  const removeSite = async (id) => {
    try {
      await api.delete(`/api/market-intel/sites/${id}`);
      await loadSites();
      await loadOffers();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Delete failed');
    }
  };

  const scrapeOne = async (site) => {
    setScraping(true);
    setProgress({ done: 0, total: 1, current: site.name, results: [] });
    try {
      const res = await api.post(`/api/market-intel/scrape/${site.id}`, null,
        { params: { force: 1 }, timeout: 240_000 });
      setProgress({ done: 1, total: 1, current: site.name, results: [res.data.data] });
    } catch (err) {
      setProgress({ done: 1, total: 1, current: site.name, results: [{
        name: site.name, status: 'fetch_failed',
        message: err.response?.data?.message || err.message, offerCount: 0 }] });
    }
    setScraping(false);
    await loadSites();
    await loadOffers();
  };

  // Trend chart: one series per site, x = day
  const chartData = useMemo(() => {
    const byDay = new Map();
    for (const h of history) {
      if (!byDay.has(h.day)) byDay.set(h.day, { day: h.day });
      byDay.get(h.day)[h.siteName] = h.avgPrice;
    }
    return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
  }, [history]);
  const chartSites = useMemo(() => [...new Set(history.map(h => h.siteName))], [history]);

  const kpi = useMemo(() => {
    const priced = offers.filter(o => o.priceAud != null && ['total', 'from'].includes(o.priceBasis));
    const perKwh = offers.filter(o => o.pricePerKwh != null).map(o => o.pricePerKwh);
    return {
      sites: sites.filter(s => s.enabled === 'YES').length,
      offers: offers.length,
      cheapest: priced.length ? Math.min(...priced.map(o => o.priceAud)) : null,
      avgPerKwh: perKwh.length ? perKwh.reduce((a, b) => a + b, 0) / perKwh.length : null,
    };
  }, [offers, sites]);

  const offerColumns = [
    { title: 'Competitor', dataIndex: 'siteName', width: 150, fixed: 'left',
      render: (v, r) => <a href={r.siteUrl} target="_blank" rel="noreferrer">{v} <LinkOutlined style={{ fontSize: 10 }} /></a> },
    { title: 'Package', dataIndex: 'packageName', width: 280, ellipsis: true,
      render: (v, r) => <Tooltip title={r.notes || v}>{v || '-'}</Tooltip> },
    { title: 'Battery', dataIndex: 'batteryBrand', width: 150,
      render: (v, r) => v ? <span>{v}{r.batteryModel ? <span style={{ color: '#999' }}> {r.batteryModel}</span> : null}</span> : '-' },
    { title: 'kWh', dataIndex: 'batteryKwh', width: 80, align: 'right',
      sorter: (a, b) => (a.batteryKwh || 0) - (b.batteryKwh || 0),
      render: v => v == null ? '-' : Number(v).toFixed(2) },
    { title: 'Solar kW', dataIndex: 'solarKw', width: 90, align: 'right',
      sorter: (a, b) => (a.solarKw || 0) - (b.solarKw || 0),
      render: v => v == null ? '-' : Number(v).toFixed(2) },
    { title: 'Price', dataIndex: 'priceAud', width: 130, align: 'right',
      sorter: (a, b) => (a.priceAud || 0) - (b.priceAud || 0),
      render: (v, r) => {
        const suffix = BASIS_LABEL[r.priceBasis];
        return <span style={{ fontWeight: 600 }}>
          {r.priceBasis === 'from' && <span style={{ color: '#999', fontWeight: 400, fontSize: 11 }}>from </span>}
          {money(v)}
          {suffix && r.priceBasis !== 'from' && <span style={{ color: '#999', fontWeight: 400, fontSize: 11 }}>{suffix}</span>}
        </span>;
      } },
    { title: '$/kWh', dataIndex: 'pricePerKwh', width: 100, align: 'right',
      sorter: (a, b) => (a.pricePerKwh || 0) - (b.pricePerKwh || 0),
      render: v => v == null ? '-' : `$${Number(v).toLocaleString()}` },
    { title: 'Terms', key: 'terms', width: 150,
      render: (_, r) => (
        <Space size={2} wrap>
          {r.includesInstall && <Tag color="green" style={{ fontSize: 10 }}>installed</Tag>}
          {r.afterRebate && <Tag color="blue" style={{ fontSize: 10 }}>after rebate</Tag>}
        </Space>
      ) },
    { title: 'Via', dataIndex: 'via', width: 110,
      render: (v, r) => {
        const web = v === 'web_search';
        const tag = <Tag color={web ? 'purple' : 'blue'} style={{ fontSize: 10 }}>
          {web ? 'web search' : 'direct'}
        </Tag>;
        return r.sourceUrl
          ? <Tooltip title={r.sourceUrl}><a href={r.sourceUrl} target="_blank" rel="noreferrer">{tag}</a></Tooltip>
          : tag;
      } },
    { title: 'Seen', dataIndex: 'observedAt', width: 110,
      render: v => v ? new Date(v).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '-' },
  ];

  const siteColumns = [
    { title: 'Competitor', dataIndex: 'name', width: 200,
      render: (v, r) => (
        <span>
          {v}
          {r.source === 'discovered' && (
            <Tooltip title="Found automatically by the web-search sweep">
              <Tag color="purple" style={{ fontSize: 10, marginLeft: 6 }}>discovered</Tag>
            </Tooltip>
          )}
        </span>
      ) },
    { title: 'Page', dataIndex: 'url', ellipsis: true,
      render: v => <a href={v} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>{v}</a> },
    { title: 'Enabled', dataIndex: 'enabled', width: 90,
      render: (v, r) => <Switch size="small" checked={v === 'YES'}
        onChange={async (checked) => {
          await api.put(`/api/market-intel/sites/${r.id}`, { enabled: checked });
          loadSites();
        }} /> },
    { title: 'Last scrape', dataIndex: 'last_status', width: 190,
      render: (v, r) => {
        const m = STATUS_META[v] || { color: 'default', text: v || 'never' };
        return (
          <Tooltip title={r.last_error || (r.last_scraped_at ? new Date(r.last_scraped_at).toLocaleString() : '')}>
            <Tag color={m.color}>{m.text}</Tag>
            {r.last_offer_count > 0 && <span style={{ fontSize: 11, color: '#999' }}>{r.last_offer_count} offers</span>}
          </Tooltip>
        );
      } },
    { title: '', key: 'act', width: 130, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Scrape now">
            <Button size="small" icon={<ReloadOutlined />} disabled={scraping} onClick={() => scrapeOne(r)} />
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(r)} />
          <Popconfirm title="Remove this competitor?" description="Price history is kept."
            onConfirm={() => removeSite(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) },
  ];

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        Advertised competitor battery packages, read from their public pages and
        structured with {meta.model || 'an LLM'}.{' '}
        <span style={{ color: '#999' }}>
          Best-effort: retailers who hide prices behind a quote form, or render
          prices in JavaScript, won't appear.
        </span>
      </div>

      {error && <Alert message={error} type="error" showIcon closable
        style={{ marginBottom: 16 }} onClose={() => setError(null)} />}

      {/* Scrape progress */}
      {scraping && (
        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <RobotOutlined style={{ color: '#54A2CC', marginRight: 6 }} />
            Reading competitor {progress.done + (progress.done < progress.total ? 1 : 0)} of {progress.total}
            {progress.current && <span style={{ color: '#666' }}> — {progress.current}</span>}
          </div>
          <Progress percent={pct} status="active" strokeColor="#54A2CC" />
        </Card>
      )}
      {!scraping && progress.results.length > 0 && (
        <Alert
          type="info" showIcon closable style={{ marginBottom: 16 }}
          onClose={() => setProgress({ done: 0, total: 0, current: '', results: [] })}
          message={`Scraped ${progress.results.length} competitor${progress.results.length === 1 ? '' : 's'}`}
          description={
            <Space wrap size={[8, 4]}>
              {progress.results.map((r, i) => {
                const m = STATUS_META[r.status] || { color: 'default', text: r.status };
                return (
                  <Tooltip key={i} title={r.message || ''}>
                    <Tag color={m.color}>
                      {r.name}: {m.text}{r.offerCount ? ` (${r.offerCount})` : ''}
                    </Tag>
                  </Tooltip>
                );
              })}
            </Space>
          }
        />
      )}

      {/* Discovery */}
      {discovering && (
        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <RobotOutlined style={{ color: '#845EC2', marginRight: 6 }} />
            Searching the web for competitors we aren't tracking yet…
            <span style={{ color: '#999' }}> this runs several searches and can take a minute</span>
          </div>
          <Progress percent={100} status="active" strokeColor="#845EC2" showInfo={false} />
        </Card>
      )}
      {!discovering && discoverResult && (
        <Alert
          type="success" showIcon closable style={{ marginBottom: 16 }}
          onClose={() => setDiscoverResult(null)}
          message={`Discovery: ${discoverResult.searchCount} web searches → ${discoverResult.offersStored} offers stored, ${discoverResult.newSites} new competitor${discoverResult.newSites === 1 ? '' : 's'} found`}
          description={
            <>
              {discoverResult.bySite?.length > 0 && (
                <Space wrap size={[8, 4]}>
                  {discoverResult.bySite.map((s, i) => (
                    <Tag key={i} color="purple">{s.name}: {s.count}</Tag>
                  ))}
                </Space>
              )}
              {discoverResult.newSites > 0 && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                  New competitors are added <b>disabled</b> — their offers are already
                  recorded, but enable one below if you also want it polled directly
                  on each refresh.
                </div>
              )}
            </>
          }
        />
      )}

      {/* KPIs */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card style={{ minHeight: 100 }}>
            <Statistic title="Competitors tracked" value={kpi.sites}
              prefix={<ShopOutlined style={{ color: '#54A2CC' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ minHeight: 100 }}>
            <Statistic title="Live offers" value={kpi.offers}
              prefix={<ThunderboltOutlined style={{ color: '#f5b759' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ minHeight: 100 }}>
            <Statistic title="Cheapest advertised" value={kpi.cheapest == null ? '-' : money(kpi.cheapest)}
              prefix={<DollarOutlined style={{ color: '#05A677' }} />}
              valueStyle={{ color: '#05A677' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card style={{ minHeight: 100 }}>
            <Statistic title="Avg $/kWh" value={kpi.avgPerKwh == null ? '-' : `$${Math.round(kpi.avgPerKwh).toLocaleString()}`} />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>battery-inclusive packages</div>
          </Card>
        </Col>
      </Row>

      {/* Trend */}
      <Card size="small" style={{ marginTop: 16 }}
        title={<span style={{ fontSize: 13 }}>Advertised price trend — avg per competitor</span>}>
        {chartData.length === 0 ? (
          <Empty description="No price history yet — add competitors and scrape to start building a trend"
            image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 8, right: 20, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" fontSize={11} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} fontSize={11} />
              <RTooltip formatter={v => money(v)} />
              <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: 8 }} />
              {chartSites.map((s, i) => (
                <Line key={s} type="monotone" dataKey={s} stroke={SITE_COLORS[i % SITE_COLORS.length]}
                  strokeWidth={2} dot={{ r: 3 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Offers */}
      <Card size="small" style={{ marginTop: 16 }}
        title={<span style={{ fontSize: 13 }}>Latest advertised packages</span>}
        extra={
          <Space size="small">
            <Tooltip title="Runs web searches to find retailers we aren't tracking yet, and records the offers it cites">
              <Button size="small" icon={<RobotOutlined />} loading={discovering} onClick={runDiscover}>
                Discover competitors
              </Button>
            </Tooltip>
            <Button size="small" type="primary" icon={<ReloadOutlined />} loading={scraping}
              style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
              onClick={() => runScrape(sites, true)}
              disabled={sites.filter(s => s.enabled === 'YES').length === 0}>
              Refresh tracked
            </Button>
          </Space>
        }>
        <Table
          dataSource={offers} rowKey="id" columns={offerColumns} loading={loading}
          size="small" scroll={{ x: 'max-content' }}
          pagination={offers.length > 20 ? { pageSize: 20, size: 'small' } : false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No offers extracted yet" /> }}
        />
      </Card>

      {/* Watch list */}
      <Card size="small" style={{ marginTop: 16 }}
        title={<span style={{ fontSize: 13 }}>Competitor watch list</span>}
        extra={<Button size="small" icon={<PlusOutlined />} onClick={() => openEditor(null)}>Add competitor</Button>}>
        <Table dataSource={sites} rowKey="id" columns={siteColumns} size="small"
          pagination={false} loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No competitors yet — add a pricing or packages page URL to start" /> }} />
        <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
          Opening this tab re-reads any <b>enabled</b> competitor not scraped in the
          last {meta.freshWindowHours || 6} hours; "Refresh tracked" ignores that
          window. Pages disallowed by robots.txt are skipped and flagged, and
          JavaScript-rendered pages yield no text — those need manual entry or a
          different URL. "Discover competitors" instead asks the model to search
          the web, so it reaches retailers we can't fetch directly.
        </div>
      </Card>

      <Modal
        title={editing ? 'Edit competitor' : 'Add competitor'}
        open={modalOpen} onOk={saveSite} onCancel={() => setModalOpen(false)}
        okText="Save" destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Competitor name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Solar Ninja" />
          </Form.Item>
          <Form.Item name="url" label="Page URL" rules={[{ required: true }]}
            extra="Link straight to their packages / pricing page, not the homepage — that's where advertised prices live.">
            <Input placeholder="https://example.com.au/solar-battery-packages" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional context" />
          </Form.Item>
          <Form.Item name="enabled" label="Include in scrapes" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
